import {colorHex, iconSymbol} from "../public/js/data/avatars.js";
import {KickButton} from "./KickButton.jsx";

export function WaitingRoom({controller, snapshot}) {
  const {game, playerId} = snapshot;
  const topPlayers = controller.topPlayers();
  const settings = controller.settingsPanel();
  const canKick = controller.canKickPlayers();
  const teams = controller.teamRoster();

  return (
    <section className="waiting-room" aria-label="Väntelobby">
      <header className="waiting-header">
        <div>
          <p className="eyebrow">Lobbykod</p>
          <h1>{game.code}</h1>
        </div>
      </header>

      {/* Read off the screen across a room, so it is the address and the code people
          type — the full link, query and all, is what Kopiera länk copies. */}
      <div className="waiting-copy">
        <p>Gå till <strong>{controller.joinHost()}</strong> på mobilen och skriv koden <strong>{game.code}</strong>.</p>
        <button className="plain-button" type="button" onClick={() => controller.copyGameLink()}>
          Kopiera länk
        </button>
      </div>

      <div className="waiting-grid">
        <section>
          <p className="eyebrow">{teams.length ? "Lagen" : "Spelare inne"}</p>
          {/* A 2 mot 2 shows the two sides instead of one flat list, with the empty
              chairs spelled out — the room needs to know how many phones are missing,
              and which side a latecomer will land on. Rows come shaped from the
              controller, so the sorting and the seating rule stay under test. */}
          {teams.length > 0 && (
            <div className="team-grid">
              {teams.map((side) => (
                <article key={side.team} className={`team-card team-${side.team}`}>
                  <p className="team-label">{side.label}</p>
                  {side.seats.map((player) => (
                    <div
                      key={player.id}
                      className={player.mine ? "team-seat me" : "team-seat"}
                      style={{"--seat-color": player.color}}
                    >
                      <span className="avatar"><span aria-hidden="true">{player.icon}</span></span>
                      <span>{player.name}</span>
                    </div>
                  ))}
                  {Array.from({length: side.open}, (_, chair) => (
                    <div key={`open-${chair}`} className="team-seat open">Tom plats</div>
                  ))}
                </article>
              ))}
            </div>
          )}
          <div className="waiting-players" hidden={teams.length > 0}>
            {game.players.length === 0 && (
              <p className="waiting-empty">Ingen här än. Den första som går med dyker upp här.</p>
            )}
            {game.players.map((player) => (
              <article
                key={player.id}
                className={`waiting-player ${player.id === playerId ? "me" : ""}`}
                style={{"--seat-color": colorHex(player.color)}}
              >
                <div className="avatar"><span aria-hidden="true">{iconSymbol(player.icon)}</span></div>
                <div className="waiting-player-info">
                  <h3>{player.name}</h3>
                  <div className="waiting-player-status">
                    <p>{controller.seatStatus(player)}</p>
                    {canKick && <KickButton controller={controller} player={player} />}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="waiting-actions">
          <p>{controller.waitingStatusText()}</p>
          {/* The host's deck rules, chosen here and dealt when the game starts. */}
          {settings.visible && (
            <div className="game-settings" aria-label="Spelinställningar">
              <div className="game-setting">
                <p className="eyebrow">Kortlekar</p>
                <div className="segmented">
                  {settings.deckModes.map((mode) => (
                    <button
                      key={mode.value}
                      className={mode.active ? "active" : ""}
                      type="button"
                      aria-pressed={mode.active}
                      onClick={() => controller.setDeckMode(mode.value)}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="game-setting">
                <p className="eyebrow">Starthand</p>
                <div className="segmented">
                  {settings.handModes.map((mode) => (
                    <button
                      key={mode.value}
                      className={mode.active ? "active" : ""}
                      type="button"
                      aria-pressed={mode.active}
                      disabled={settings.handDisabled}
                      onClick={() => controller.setOpeningHandMode(mode.value)}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="game-setting-note">{settings.note}</p>
            </div>
          )}
          <button
            className="plain-button"
            type="button"
            hidden={!controller.canAddBot()}
            disabled={controller.botRosterFull()}
            onClick={() => controller.addBot()}
          >
            Lägg till bot
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={!controller.canStartGame()}
            onClick={() => controller.startGame()}
          >
            Starta spel
          </button>
          <button className="plain-button" type="button" onClick={() => controller.leaveGame()}>
            Lämna lobby
          </button>
        </section>
      </div>

      {/* All-time board. Empty without a database, and then it simply is not there —
          the waiting room looks exactly as it did before statistics existed. */}
      {topPlayers.length > 0 && (
        <section className="leaderboard" aria-label="Topplista">
          <p className="eyebrow">Topplista</p>
          <ol className="leaderboard-list">
            {topPlayers.map((entry) => (
              <li key={entry.key} className="leaderboard-row">
                <span className="leaderboard-rank">{entry.rank}</span>
                <span className="leaderboard-name">{entry.name}</span>
                <span className="leaderboard-record">{entry.record}</span>
                <span className="leaderboard-sips">{entry.sips}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </section>
  );
}
